import { Fragment } from 'react';
import {
  reformatContentfulLocaleToLocale,
  reformatLocaleToContentfulLocale,
} from 'lib/utils/helpers';
import Head from 'next/head';
import getSchema from 'lib/utils/getSchema';

const siteName = process.env.NEXT_PUBLIC_SITENAME;
const siteUrl = process.env.NEXT_PUBLIC_SITEURL;

const WEBSITE_PATH_FRANCE = 'fr';
const WEBSITE_PATH_GENERIC_FRENCH = 'fr-fr';

/** Remove /en from canonical URL, since that is better for SEO purposes */
const formatUrl = (locale, seoSlug) => {
  const realLocale = locale === 'en' ? null : locale;

  if (realLocale === null) return `${siteUrl}/${`${seoSlug}` === '/' ? '' : `${seoSlug}/`}`;

  return `${siteUrl}/${realLocale}/${`${seoSlug}` === '/' ? '' : `${seoSlug}/`}`;
};

const SEO = ({ seo, pageData, product }) => {
  const title = seo ? seo?.title : siteName;
  const description = seo ? seo.description : '';
  const keywords = seo && seo?.keywords?.length >= 1 ? seo?.keywords?.map(word => word) : '';
  const ogImage = seo ? seo?.ogImage : '';
  const realLocale = reformatContentfulLocaleToLocale(seo?.locale) || '';
  const url = seo?.slug ? formatUrl(realLocale, seo.slug) : '';
  let canonicalUrl = url;
  if (product) {
    canonicalUrl = seo?.slug ? formatUrl(realLocale, `p/${seo.slug}`) : '';
  }
  const robotsContent =
    (seo?.robots?.includes('noindex') ||
      seo?.robots?.includes('nofollow') ||
      seo?.robots?.includes('noindex,nofollow')) &&
    seo.robots;

  const decideHref = (hrefLocale, slug) => {
    const isXDefault = hrefLocale === 'en';
    if (isXDefault) return `${siteUrl}/${slug === '/' ? '' : `${slug}/`}`;

    // replace back to 'na' because there is no 'us' locale
    return `${siteUrl}/${hrefLocale?.replace('us', 'na')}/${slug === '/' ? '' : `${slug}/`}`;
  };

  const decideHrefLang = hrefLangLocale => {
    const lang = reformatLocaleToContentfulLocale(hrefLangLocale);

    return lang.toLowerCase();
  };

  // adding us to have en-us. Defensive: some hub-driven pages parse SEO without
  // a real href list yet; treat that as "no hreflang alternates" rather than
  // crashing on a null spread.
  const seoHrefs = (Array.isArray(seo?.href) ? seo.href.filter(Boolean) : []).filter(
    ({ locale }) => locale !== WEBSITE_PATH_GENERIC_FRENCH
  );
  const franceHref = seoHrefs.find(
    ({ locale, isCanonicalTarget }) => locale === WEBSITE_PATH_FRANCE && isCanonicalTarget
  );
  if (realLocale === WEBSITE_PATH_GENERIC_FRENCH && franceHref?.slug) {
    canonicalUrl = formatUrl(WEBSITE_PATH_FRANCE, franceHref.slug);
  }

  const schema = getSchema(pageData);

  return (
    <Head>
      <title>{title}</title>
      <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta name="description" content={description} />
      <meta name="image" content={ogImage} />
      <meta name="keywords" content={keywords} />
      <meta name="twitter:site" content={siteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image:src" content={ogImage} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={url} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:title" content={title} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={seo ? realLocale : ''} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {seoHrefs &&
        seoHrefs?.length >= 0 &&
        seoHrefs.map(({ locale: hrefLocale, slug }) => (
          <Fragment key={hrefLocale}>
            <link
              rel="alternate"
              hrefLang={decideHrefLang(hrefLocale)}
              href={decideHref(hrefLocale, slug)}
            />
            {hrefLocale === 'en' && (
              <link rel="alternate" hrefLang="x-default" href={decideHref('en', slug)} />
            )}
          </Fragment>
        ))}
      {schema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      )}
    </Head>
  );
};

export default SEO;
